/**
 * Confronto costo tra 2+ RSA per la stessa mansione (o gruppo custom) —
 * pivot RSA × mansione/gruppo, per individuare discrepanze (es. costo/ora
 * o costo/ospite molto diverso tra RSA per lo stesso ruolo). Sezione
 * separata da CostoPersonalePage (analisi singola RSA) — workflow diverso.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import {
  Card,
  Spinner,
  ErrorBox,
  SimpleTable,
  Badge,
} from "../components/ui/basics";
import { euro, ore } from "./costoPersonale/format";

const DIMENSIONI = [
  { value: "mansione", label: "Mansione" },
  { value: "gruppoCosto", label: "Gruppo mansioni" },
];

// Sopra questa soglia lo scarto % viene evidenziato come possibile anomalia
// da verificare — non un errore, solo un segnale per l'occhio.
const SOGLIA_SCARTO_PCT = 15;

export default function CostoPersonaleConfrontoPage() {
  const { data: filtri } = useApi("/costo-personale/filtri");
  const { data: catalogoMansioni } = useApi("/planning/mansioni");
  const [rsaSelezionate, setRsaSelezionate] = useState([]);
  const [periodoDa, setPeriodoDa] = useState("");
  const [periodoA, setPeriodoA] = useState("");
  const [dimensione, setDimensione] = useState("mansione");

  const nomeMansione = (codice) =>
    (catalogoMansioni?.items || []).find((m) => m.codice === codice)?.nome ||
    codice;

  const toggleRsa = (rsa) => {
    setRsaSelezionate((sel) =>
      sel.includes(rsa) ? sel.filter((r) => r !== rsa) : [...sel, rsa],
    );
  };

  const endpoint = useMemo(() => {
    if (rsaSelezionate.length < 2) return null;
    const p = new URLSearchParams();
    p.set("rsa", rsaSelezionate.join(","));
    p.set("dimensione", dimensione);
    if (periodoDa) p.set("periodoDa", periodoDa);
    if (periodoA) p.set("periodoA", periodoA);
    return `/costo-personale/confronto?${p.toString()}`;
  }, [rsaSelezionate, dimensione, periodoDa, periodoA]);

  const { data: confronto, loading, error } = useApi(endpoint, [endpoint]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Confronto costo tra RSA
          </h1>
          <p className="text-sm text-gray-500">
            Seleziona 2 o più RSA per confrontare il costo per mansione (o
            gruppo) e individuare eventuali discrepanze.
          </p>
        </div>
        <Link
          to="/costo-personale"
          className="shrink-0 px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          ← Torna all'analisi
        </Link>
      </div>

      <Card title="Filtri">
        <div className="space-y-3">
          <div>
            <span className="block text-xs text-gray-500 mb-1">
              RSA da confrontare ({rsaSelezionate.length} selezionate, minimo 2)
            </span>
            <div className="flex flex-wrap gap-2">
              {(filtri?.rsa || []).map((r) => (
                <label
                  key={r}
                  className="flex items-center gap-1 text-xs bg-gray-50 rounded px-2 py-1 cursor-pointer border border-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={rsaSelezionate.includes(r)}
                    onChange={() => toggleRsa(r)}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Periodo da
              </label>
              <input
                type="month"
                value={periodoDa}
                min={filtri?.periodoMin}
                max={filtri?.periodoMax}
                onChange={(e) => setPeriodoDa(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Periodo a
              </label>
              <input
                type="month"
                value={periodoA}
                min={filtri?.periodoMin}
                max={filtri?.periodoMax}
                onChange={(e) => setPeriodoA(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Confronta per
              </label>
              <select
                value={dimensione}
                onChange={(e) => setDimensione(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                {DIMENSIONI.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Card>

      {rsaSelezionate.length < 2 && (
        <p className="text-sm text-gray-400">
          Seleziona almeno 2 RSA per vedere il confronto.
        </p>
      )}
      {loading && <Spinner label="Calcolo confronto…" />}
      {error && <ErrorBox message={error} />}

      {confronto && (
        <Card
          subtitle={`${confronto.righe.length} ${
            dimensione === "gruppoCosto" ? "gruppi" : "mansioni"
          } con dati su almeno una RSA selezionata`}
        >
          <SimpleTable
            rowKey={(r) => r.chiave}
            columns={[
              {
                key: "chiave",
                label: dimensione === "gruppoCosto" ? "Gruppo" : "Mansione",
                render: (r) =>
                  dimensione === "gruppoCosto"
                    ? r.nome || r.chiave
                    : nomeMansione(r.chiave),
              },
              ...confronto.rsaList.map((rsaNome) => ({
                key: rsaNome,
                label: rsaNome,
                align: "right",
                render: (r) => {
                  const cella = r.perRsa[rsaNome];
                  if (!cella || cella.righe === 0) {
                    return <span className="text-gray-300">—</span>;
                  }
                  return (
                    <div className="leading-tight">
                      <div>{euro(cella.costoTotale)}</div>
                      <div className="text-xs text-gray-400">
                        {euro(cella.costoMedioOrario)}/h ·{" "}
                        {ore(cella.oreTotali)}h
                      </div>
                      {cella.costoPerOspite != null && (
                        <div className="text-xs text-gray-400">
                          {euro(cella.costoPerOspite)}/ospite
                        </div>
                      )}
                    </div>
                  );
                },
              })),
              {
                key: "scarto",
                label: "Scarto",
                align: "right",
                render: (r) => {
                  const scarto = r.scartoPerOspite || r.scartoOrario;
                  if (!scarto || scarto.scartoPct == null) {
                    return <span className="text-gray-300">—</span>;
                  }
                  const anomalo = scarto.scartoPct >= SOGLIA_SCARTO_PCT;
                  return (
                    <div>
                      <Badge status={anomalo ? "attenzione" : "default"}>
                        {scarto.scartoPct}%
                      </Badge>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {r.scartoPerOspite ? "€/ospite" : "€/ora"}
                      </div>
                    </div>
                  );
                },
              },
            ]}
            rows={confronto.righe}
          />
        </Card>
      )}
    </div>
  );
}
